import Component from "@glimmer/component";
import { service } from "@ember/service";
import { on } from "@ember/modifier";
import { task } from "ember-concurrency";
import { t } from "ember-intl";
import { and, gt } from "ember-truth-helpers";
import { saveRecord } from "@warp-drive/legacy/compat/builders";
import can from "ember-can/helpers/can";
import canManage from "glesys-controlpanel/helpers/can-manage";
import modal from "glesys-controlpanel/helpers/modal";
import PageContent from "glesys-controlpanel/components/page-content";
import PageContentServiceIntro from "glesys-controlpanel/components/page-content/service-intro";
import GButtonIcon from "glesys-controlpanel/components/g-button/icon";
import IconsPlus from "glesys-controlpanel/components/icons/plus";
import EmptyState from "glesys-controlpanel/components/empty-state";
import Entity from "glesys-controlpanel/components/entity";
import EntityField from "glesys-controlpanel/components/entity/fields/field";
import EntityDataCenterField from "glesys-controlpanel/components/entity/fields/data-center";
import EntityList from "glesys-controlpanel/components/entity-list";
import CostPlain from "glesys-controlpanel/components/cost/plain";
import { array, get } from "@ember/helper";
import IconsSidebarNetworking from "glesys-controlpanel/components/icons/sidebar/networking";

const allAddresses = (addresses) => addresses.join(", ");
const extraCount = (addresses) => addresses.length - 1;

const Addresses = <template>
  {{#if @addresses}}
    <span title={{allAddresses @addresses}}>
      {{get @addresses 0}}
      {{#if (gt @addresses.length 1)}}
        <span class="text-gray-500">+{{extraCount @addresses}}</span>
      {{/if}}
    </span>
  {{else}}
    —
  {{/if}}
</template>;

export default class LoadBalancersOverview extends Component {
  @service router;
  @service store;

  get project() {
    return this.args.model.project;
  }

  get loadBalancers() {
    return this.args.model.loadBalancers;
  }

  get savedLoadBalancers() {
    return this.loadBalancers.filter((loadBalancer) => loadBalancer.id);
  }

  createLoadBalancerTask = task(async (name, dataCenter) => {
    let project = this.project;

    let loadBalancer = this.store.createRecord("load-balancer", {
      project,
      name,
      datacenter: dataCenter,
    });

    await this.store.request(saveRecord(loadBalancer)).catch((error) => {
      loadBalancer.unloadRecord();
      throw error;
    });

    this.router.transitionTo("load-balancer", loadBalancer.id);
  });

  <template>
    <PageContent>
      <PageContentServiceIntro @title={{t "load-balancers.page-title"}}>
        {{#if (and this.savedLoadBalancers (can "create load-balancer for project" this.project))}}
          <GButtonIcon
            data-test-load-balancer-create-button
            @display="default"
            @color="green"
            {{on "click" (modal "modals/load-balancers-create" task=this.createLoadBalancerTask)}}
          >
            <IconsPlus class="w-4 -ml-1 mr-1" />
            {{t "button.create"}}
          </GButtonIcon>
        {{/if}}
      </PageContentServiceIntro>

      <EntityList @items={{this.savedLoadBalancers}}>
        <:default as |loadBalancer|>
          <Entity
            @route="load-balancer"
            @model={{loadBalancer.id}}
            @title={{loadBalancer.name}}
            @key={{loadBalancer.id}}
            @linkLabel={{t "load-balancers.manage-load-balancer-aria-label" name=loadBalancer.name}}
            @managedHosting={{loadBalancer.isManagedHosting}}
          >
            <:fields>
              <EntityDataCenterField @dataCenter={{loadBalancer.datacenter}} />
              <EntityField data-test-load-balancer-ipv4 @label={{t "label.ipv4"}}>
                <Addresses @addresses={{loadBalancer.ipv4Addresses}} />
              </EntityField>
              <EntityField data-test-load-balancer-ipv6 @label={{t "label.ipv6"}}>
                <Addresses @addresses={{loadBalancer.ipv6Addresses}} />
              </EntityField>
              <EntityField data-test-load-balancer-cost @label={{t "label.cost"}}>
                <CostPlain @currency={{loadBalancer.cost.currency}} @price={{loadBalancer.cost.amount}} />
              </EntityField>
            </:fields>
          </Entity>
        </:default>
        <:empty>
          <EmptyState
            @icon={{IconsSidebarNetworking}}
            @title={{t "load-balancers.empty-state.title"}}
            @description={{t "load-balancers.empty-state.description"}}
            @points={{array
              (t "load-balancers.empty-state.usp.health-check")
              (t "load-balancers.empty-state.usp.two-protocols")
              (t "load-balancers.empty-state.usp.ssl")
            }}
            @learnMoreUrl="https://docs.glesys.com/products/connectivity/load-balancer"
            @learnMoreAriaLabel={{t "load-balancers.empty-state.learn-more-aria-label"}}
            @createLabel={{t "load-balancers.empty-state.button"}}
            @onCreate={{if (canManage) (modal "modals/load-balancers-create" task=this.createLoadBalancerTask)}}
          />
        </:empty>
      </EntityList>
    </PageContent>
  </template>
}
